// A 30 s default timeout for READS ONLY.
//
// Nothing in the app set a timeout, so a request the server never answered
// left its screen waiting for ever. A GET that sets no timeout of its own, is
// not a download and is not on NO_TIMEOUT_GETS below now gives up after 30 s.
//
// Writes never time out here. A browser timeout stops the wait, not the
// server: a booking or label call still running past 30 s would look failed,
// and a retry could book or print it twice. Uploads keep their own 360 s and
// contactService its 20 s, because a call's own timeout always wins.
//
// A timeout reads exactly as a network failure, because no screen has words
// for a timeout: axios is given its own network-failure message, and the
// fetch helper fails the way fetch does when the network is down.
//
// Importing this module installs the rule on axios itself; index.js imports
// it first, before anything can make a request. httpClient.js installs it on
// its own instance.
import axios from 'axios';
import { API_HOST, HTTP_PROTOCOL } from '../../config';

export const GET_TIMEOUT_MS = 30000;

// What axios's browser adapter says when the network fails.
const AXIOS_NETWORK_FAILURE = 'Network Error';
// What fetch rejects with when the network fails (Chrome's words).
const FETCH_NETWORK_FAILURE = 'Failed to fetch';
const DOWNLOADS = ['blob', 'arraybuffer', 'document', 'stream'];

const lower = value => String(value === undefined || value === null ? '' : value).toLowerCase();

// The GETs that keep no limit. In this codebase a GET is
// not always a plain read, and only a plain read (class 1) is cut off at 30 s.
// These keep today's behaviour:
//   (2) the server builds something, or calls a carrier or another service;
//   (3) the server writes, so a retry after a cut-off could write twice;
//   (4) a slow read with no measured timing yet, where a false failure is
//       worse for staff than the wait. Measured limits can replace these.
// `path` is the path after the API root, matched on whole segments, '*'
// standing for any one segment. `when`, where given, narrows an entry to the
// calls whose query (or axios params) the server reads the same way.
// `server` is the line that decides the class.
export const NO_TIMEOUT_GETS = [
    { path: 'booking/get_booking', class: '3 and 2', server: 'api/views.py:4942-4968',
        reason: 'with no quotes on the booking, starts a background re-quote of every carrier' },
    { path: 'pricing/get_pricings', class: '3', server: 'api/views.py:10387',
        reason: 'on an AusPost booking, saves the routing fields on every call (fp_apis/routing_context.py:470, 476)' },
    { path: 'pricing/get_pricing_status', class: '3 and 2', server: 'api/operations/pricing_freshness.py:348-364',
        reason: 'when pricing is stale, writes the pricing status and starts a background re-quote' },
    { path: 'bookings/send_email', class: '3 and 2', server: 'api/operations/email_senders.py:512-521',
        reason: 'sends the booking status email and logs it' },
    { path: 'booking/get_labels_bookings', class: '3 and 2', server: 'api/views.py:6920-6926',
        reason: 'builds and saves a merged label PDF; for Aberdeen it also sets a merge lock' },
    { path: 'booking/get_status_page_url', class: '3', server: 'api/views.py:7871',
        reason: 'saves the client booking reference when it is blank' },
    { path: 'fr8beacon/tag-journey', class: '2', server: 'api/views.py:25775-25785',
        reason: 'asks the GPS vendor twice' },
    { path: 'fr8beacon/battery-detail', class: '2', server: 'api/views.py:26122',
        reason: 'asks the GPS vendor on a cache miss' },
    { path: 'zendesk/tickets', class: '2 and 3', server: 'api/operations/zendesk/tickets.py:132-153',
        reason: 'asks Zendesk every time, and saves a refreshed token when it has expired' },
    { path: 'zendesk/opened_ticket_count', class: '2 and 3', server: 'api/operations/zendesk/helpers.py:1341',
        reason: 'asks Zendesk, and saves a refreshed token when it has expired' },
    { path: 'client-notify-history/*/preview', class: '2', server: 'api/operations/emails/email_preview.py:47',
        reason: 'fetches the email template from S3' },
    { path: 'get_delivery_status', class: '3', server: 'api/views_client.py:1916-1923',
        reason: 'works out and saves the ETAs when the booking has none' },
    { path: 'bok_1_headers/send_email', class: '3 and 2', server: 'api/views_client.py:439-456',
        reason: 'sends the picking slip email; a retry sends it again' },
    { path: 'clients/get_sub_clients', class: '3', server: 'api/views.py:13144-13148',
        reason: 'promotes a due client reference and saves it' },
    { path: 'consolidation/workbench', class: '3', server: 'api/views_consolidation.py:794-812',
        reason: 'creates cluster rows and saves each cluster profile' },
    { path: 'consolidation/clusters/*', class: '3', server: 'api/views_consolidation.py:887-897',
        reason: 'recomputes and saves the cluster totals on every call' },
    { path: 'address-book', class: '2', server: 'api/operations/contact/contact.py:223',
        reason: 'every list is a search on AWS OpenSearch' },
    { path: 'unsubscribe', class: '3 and 2', server: 'api/views_client.py:2352-2362',
        reason: 'writes two rows and calls AWS SES' },
    { path: 'fr8beacon/device-config', class: '2', server: 'api/views_fr8beacon_device.py:1700-1731',
        reason: 'up to two GPS vendor calls on a cold cache' },
    { path: 'get-attachment', class: '2', server: 'api/views.py:11754',
        reason: 'the server fetches the attachment and parses it' },
    // Query-aware, for the Fr8Beacon console's raw fetches.
    { path: 'fr8beacon/geofence', class: '3 and 2', server: 'api/views.py:26713-26714',
        when: q => ['1', 'true', 'yes'].indexOf(lower(q.refresh)) > -1,
        reason: 'with refresh=1, asks the GPS vendor (:26752) and saves the fence (:26646); without it, a plain read' },
    { path: 'fr8beacon/device-zones', class: '2', server: 'api/views_fr8beacon_device.py:2930-2931',
        when: q => ['0', 'false', 'no'].indexOf(lower(q.carrier === undefined ? '1' : q.carrier)) === -1,
        reason: 'unless carrier=0, checks the GPS vendor (:2944-2963); an absent carrier counts as 1' },
    // Slow reads, not measured.
    { path: 'bookings/get_bookings', class: '4', server: 'api/views.py:977-2749',
        reason: 'the main bookings list, the heaviest read' },
    { path: 'bookings/get_status_info', class: '4', server: 'api/views.py:3882',
        reason: 'one count per status' },
    { path: 'bok_3_lines_data', class: '4', server: 'api/views_client.py:676',
        reason: 'every BOK 3 row, with no limit' },
    { path: 'logs', class: '4', server: 'api/views_external_apis.py:99-102',
        reason: 'reads the whole debug.log to return its last 300 lines' },
    { path: 'clients/get_clients_fp_map', class: '4', server: 'api/views.py:12920',
        reason: 'one query per client' },
    { path: 'bookingsets', class: '4', server: 'api/views.py:12317-12331',
        when: q => q.bookingId !== undefined && q.bookingId !== '',
        reason: 'with bookingId, scans every booking set; the paged list is a plain read' },
    { path: 'bookings/get_manifest_report', class: '4', server: 'api/views.py:3969-4071',
        reason: 'a nested loop over 20 days of manifests' },
];

const API_ROOT = HTTP_PROTOCOL + '://' + API_HOST;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function decode(text) {
    try {
        return decodeURIComponent(text.replace(/\+/g, ' '));
    } catch (e) {
        return text;
    }
}

// The path after the API root, as segments, and the query; null for a URL
// outside the API. axios params are read after the URL's own query, as the
// server reads a repeated name: the last one wins.
function apiRequest(url, params, baseURL) {
    let full = String(url || '');
    if (!HAS_SCHEME.test(full) && baseURL) {
        full = String(baseURL).replace(/\/+$/, '') + '/' + full.replace(/^\/+/, '');
    }
    if (full.indexOf(API_ROOT) !== 0 || '/?#'.indexOf(full.charAt(API_ROOT.length)) === -1) {
        return null;
    }
    const rest = full.slice(API_ROOT.length).split('#')[0];
    const cut = rest.indexOf('?');
    const query = {};
    if (cut > -1) {
        rest.slice(cut + 1).split('&').forEach((pair) => {
            if (!pair) return;
            const eq = pair.indexOf('=');
            query[decode(eq > -1 ? pair.slice(0, eq) : pair)] = eq > -1 ? decode(pair.slice(eq + 1)) : '';
        });
    }
    if (params && typeof params === 'object') {
        Object.keys(params).forEach((key) => {
            if (params[key] !== undefined && params[key] !== null) {
                query[key] = String(params[key]);
            }
        });
    }
    const segments = (cut > -1 ? rest.slice(0, cut) : rest).split('/').filter(Boolean);
    return { segments, query };
}

export function keepsNoLimit(url, params, baseURL) {
    const req = apiRequest(url, params, baseURL);
    if (!req) return false;
    return NO_TIMEOUT_GETS.some((entry) => {
        const want = entry.path.split('/');
        return want.length === req.segments.length
            && want.every((segment, i) => segment === '*' || segment === req.segments[i])
            && (!entry.when || entry.when(req.query));
    });
}

// An axios request interceptor.
export function withGetTimeout(config) {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' || config.timeout || DOWNLOADS.indexOf(config.responseType) > -1
        || keepsNoLimit(config.url, config.params, config.baseURL)) {
        return config;
    }
    config.timeout = GET_TIMEOUT_MS;
    config.timeoutErrorMessage = AXIOS_NETWORK_FAILURE;
    return config;
}

// fetch() with the same rule, for the raw fetch GETs.
export function fetchWithGetTimeout(input, init) {
    const options = init || {};
    const method = (options.method || 'GET').toUpperCase();
    const url = input && typeof input === 'object' && input.url ? input.url : String(input || '');
    if (method !== 'GET' || options.signal || typeof AbortController === 'undefined' || keepsNoLimit(url)) {
        return fetch(input, options);
    }
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, GET_TIMEOUT_MS);
    return fetch(input, Object.assign({}, options, { signal: controller.signal })).then(
        (response) => {
            clearTimeout(timer);
            return response;
        },
        (error) => {
            clearTimeout(timer);
            throw timedOut ? new TypeError(FETCH_NETWORK_FAILURE) : error;
        }
    );
}

axios.interceptors.request.use(withGetTimeout);



// WEBPACK FOOTER //
// src/state/services/getTimeout.js